// @flow
import {
  ActionCreator,
  buildCatchError,
  createReducer,
} from '../../../store/utils';
import {ofType} from 'redux-observable';
import {map, switchMap, debounceTime, catchError} from 'rxjs/operators';
import {of} from 'rxjs';
import {
  getCouponsApi,
  getCouponApi,
  editCouponApi,
  createCouponApi,
  endpoints,
} from './api';
import {buildRoute} from '../../../http';

import type {RootState} from '../../index';
import type {ActionsObservable, StateObservable} from 'redux-observable';
import type {Action} from '../../../store/utils';
import type {ApiError} from '../../../http/error';
import type {Coupon, CouponState} from '../../../entities';
import type {CouponRequest} from './api';

export type CouponsAction = Action & {
  coupons?: Coupon[],
  coupon?: Coupon,
  couponRequest?: CouponRequest,
  error?: string | ApiError,
  errorCode?: string,
  couponId?: string,
};

export type CouponsState = {
  isFetchingCoupons?: boolean,
  isFetchingCoupon?: boolean,
  isSaving?: boolean,
  coupon?: Coupon,
  coupons?: Coupon[],
  error?: string,
  errorCode?: string,
  createCouponSuccess?: boolean,
  updateCouponSuccess?: boolean,
  deleteCouponSuccess?: boolean,
  selectedState?: CouponState,
};

export class ResetState extends ActionCreator<CouponsAction> {}
export class CouponNoop extends ActionCreator<CouponsAction> {}

export class FetchCoupons extends ActionCreator<CouponsAction> {}
export class FetchCouponsSuccess extends ActionCreator<CouponsAction> {}
export class FetchCouponsErrorFn extends ActionCreator<CouponsAction> {}

export class FetchCoupon extends ActionCreator<CouponsAction> {}
export class FetchCouponSuccess extends ActionCreator<CouponsAction> {}
export class FetchCouponErrorFn extends ActionCreator<CouponsAction> {}

export class CreateCoupon extends ActionCreator<CouponsAction> {}
export class CreateCouponSuccess extends ActionCreator<CouponsAction> {}
export class CreateCouponErrorFn extends ActionCreator<CouponsAction> {}

export class UpdateCoupon extends ActionCreator<CouponsAction> {}
export class UpdateCouponSuccess extends ActionCreator<CouponsAction> {}
export class UpdateCouponErrorFn extends ActionCreator<CouponsAction> {}

export class DeleteCoupon extends ActionCreator<CouponsAction> {}
export class DeleteCouponSuccess extends ActionCreator<CouponsAction> {}
export class DeleteCouponErrorFn extends ActionCreator<CouponsAction> {}

export class ResetCoupon extends ActionCreator<CouponsAction> {}
export class CleanCoupon extends ActionCreator<CouponsAction> {}

export class ToggleFilter extends ActionCreator<CouponsAction> {}

export const actions = {
  resetState: () => new ResetState(),
  toggleFilter: (couponState?: CouponState) =>
    new ToggleFilter({
      payload: couponState,
    }),
  fetchCoupons: (payload: CouponsAction) => new FetchCoupons(payload),
  fetchCoupon: (payload: CouponsAction) => new FetchCoupon(payload),
  createCoupon: (payload: CouponsAction) => new CreateCoupon(payload),
  updateCoupon: (payload: CouponsAction) => new UpdateCoupon(payload),
  deleteCoupon: (payload: CouponsAction) => new DeleteCoupon(payload),
  resetCoupon: () => new ResetCoupon(),
  cleanCoupon: () => new CleanCoupon(),
};
export const initialState: CouponsState = {selectedState: '1'};

export default createReducer<CouponsState>(initialState, {
  [CouponNoop.type]: (state: CouponsState) => state,
  [ResetState.type]: () => initialState,
  [FetchCoupons.type]: (state: CouponsState) => ({
    ...state,
    isFetchingCoupons: true,
  }),
  [FetchCouponsSuccess.type]: (state: CouponsState, action: CouponsAction) => ({
    ...state,
    isFetchingCoupons: false,
    error: undefined,
    coupons: action.data,
  }),
  [FetchCouponsErrorFn.type]: (state: CouponsState, action: CouponsAction) => ({
    ...state,
    isFetchingCoupons: false,
    error: action.error,
  }),
  [FetchCoupon.type]: (state: CouponsState) => ({
    ...state,
    coupon: undefined,
    isFetchingCoupon: true,
  }),
  [FetchCouponSuccess.type]: (state: CouponsState, action: CouponsAction) => ({
    ...state,
    isFetchingCoupon: false,
    error: undefined,
    coupon: action.data,
  }),
  [FetchCouponErrorFn.type]: (state: CouponsState, action: CouponsAction) => ({
    ...state,
    isFetchingCoupon: false,
    error: action.error,
  }),
  [CreateCoupon.type]: (state: CouponsState) => ({
    ...state,
    createCouponSuccess: undefined,
    isSaving: true,
    error: undefined,
    errorCode: undefined,
  }),
  [CreateCouponSuccess.type]: (state: CouponsState, action: CouponsAction) => ({
    ...state,
    coupons: [...(state.coupons || []), action.data],
    isSaving: false,
    error: undefined,
    errorCode: undefined,
    createCouponSuccess: true,
    coupon: action.data,
  }),
  [CreateCouponErrorFn.type]: (state: CouponsState, action: CouponsAction) => ({
    ...state,
    isSaving: false,
    createCouponSuccess: undefined,
    error: action.error,
    errorCode: action.errorCode,
  }),
  [UpdateCoupon.type]: (state: CouponsState, action: CouponsAction) => ({
    ...state,
    isSaving: true,
    updateCouponSuccess: undefined,
    error: undefined,
    errorCode: undefined,
  }),
  [UpdateCouponSuccess.type]: (state: CouponsState, action: CouponsAction) => {
    const aux = [...(state.coupons || [])];
    const index = aux.findIndex(c => c.id === action.data.id);
    if (index > -1) {
      aux[index] = action.data;
    }
    return {
      ...state,
      coupons: aux,
      isSaving: false,
      updateCouponSuccess: true,
      error: undefined,
      errorCode: undefined,
      coupon: action.data,
    };
  },
  [UpdateCouponErrorFn.type]: (state: CouponsState, action: CouponsAction) => ({
    ...state,
    isSaving: false,
    updateCouponSuccess: undefined,
    error: action.error,
    errorCode: action.errorCode,
  }),
  [DeleteCoupon.type]: (state: CouponsState) => ({
    ...state,
    deleteCouponSuccess: undefined,
    isSaving: true,
  }),
  [DeleteCouponSuccess.type]: (state: CouponsState, action: CouponsAction) => ({
    ...state,
    coupons: state.coupons
      ? state.coupons.filter(c => c.id !== action.id)
      : undefined,
    isSaving: false,
    error: undefined,
    deleteCouponSuccess: true,
    coupon: undefined,
  }),
  [DeleteCouponErrorFn.type]: (state: CouponsState, action: CouponsAction) => ({
    ...state,
    isSaving: false,
    deleteCouponSuccess: undefined,
    error: action.error,
  }),
  [ResetCoupon.type]: (state: CouponsState) => ({
    ...state,
    coupon: undefined,
    deleteCouponSuccess: undefined,
    error: undefined,
    errorCode: undefined,
  }),
  [CleanCoupon.type]: (state: CouponsState) => ({
    ...state,
    coupon: undefined,
    updateCouponSuccess: undefined,
    createCouponSuccess: undefined,
    deleteCouponSuccess: undefined,
    error: undefined,
    errorCode: undefined,
  }),
  [ToggleFilter.type]: (state: CouponsState, {payload}: CouponsAction) => ({
    ...state,
    selectedState: payload || '',
  }),
});

export const fetchCouponsEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(FetchCoupons.type, UpdateCouponSuccess.type),
    debounceTime(250),
    switchMap(() => {
      const selectedState = state$.value.coupons.selectedState;
      const queryFilter = selectedState !== '' ? {enabled: selectedState} : {};

      return getCouponsApi(queryFilter).pipe(
        map(data => new FetchCouponsSuccess({data: data})),
        buildCatchError(FetchCouponsErrorFn, 'Error al obtener los cupones')
      );
    })
  );

export const fetchCouponEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchCoupon.type),
    debounceTime(250),
    switchMap(({couponId}: CouponsAction) =>
      getCouponApi(undefined, {
        route: buildRoute(endpoints.GET_COUPON, [couponId]),
      }).pipe(
        map(data => new FetchCouponSuccess({data: data})),
        buildCatchError(FetchCouponErrorFn)
      )
    )
  );

export const createCouponEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(CreateCoupon.type),
    switchMap(({couponRequest}: {couponRequest: CouponRequest}) =>
      createCouponApi(couponRequest).pipe(
        map(data => new CreateCouponSuccess({data: data})),
        buildCatchError(CreateCouponErrorFn, 'Código de cupón ya existe')
      )
    )
  );

export const COUPON_ALREADY_USED_ERROR = 'CouponAlreadyUsedError';
export const COUPON_ALREADY_USED_MESSAGE =
  'No se pudieron guardar los cambios porque este cupón ya fue utilizado en un pedido. Solo puedes modificar el límite de usos y la fecha de expiración.';

export const updateCouponEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(UpdateCoupon.type),
    switchMap(
      ({
        couponRequest,
        couponId,
      }: {
        couponRequest: CouponRequest,
        couponId: string,
      }) =>
        editCouponApi(
          {
            ...couponRequest,
          },
          {
            route: buildRoute(endpoints.EDIT_COUPON, [couponId]),
          }
        ).pipe(
          map(data => new UpdateCouponSuccess({data: data})),
          catchError((error: ApiError) => {
            if (
              error &&
              error.error &&
              error.error.name === COUPON_ALREADY_USED_ERROR
            ) {
              return of(
                new UpdateCouponErrorFn({
                  error: COUPON_ALREADY_USED_MESSAGE,
                  errorCode: COUPON_ALREADY_USED_ERROR,
                })
              );
            }
            return of(
              new UpdateCouponErrorFn({
                error: 'Error al actualizar cupón',
              })
            );
          })
        )
    )
  );

export const deleteCouponEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(DeleteCoupon.type),
    switchMap(({couponRequest}: {couponRequest: CouponRequest}) => {
      return createCouponApi(couponRequest).pipe(
        map(({data}: {data: Coupon}) => new DeleteCouponSuccess({data})),
        buildCatchError(DeleteCouponErrorFn, 'Error al editar el cupón')
      );
    })
  );
